import { Avatar, Button, Input, Listy, Skeleton, Spin, Tag, Tooltip } from 'antd';
import clsx from 'clsx';
import type File from '../../../interfaces/File';
import type TagType from '../../../interfaces/Tag';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileLimitationEnum } from '../../../enums/FileLimitationEnum';
import { FileSalesStatusEnum } from '../../../enums/FileSalesStatusEnum';
import { DESCRIPTION_CHARACTERS_LIMIT, useFileDetailTab } from '../../../hooks/useFileDetailTab';
import { useColorScheme } from '../../../hooks/useColorScheme/useColorScheme';
import { getTagPresentationStyle } from '../../../utils/colorsUtilis';
import {
  copyToClipboard,
  dateFromTicks,
  formatFileSize,
  getFolderHex,
  getPurchaseLink,
  hasFileLimitation,
  printAmount,
  printCurrency,
  removeShortcutExtension,
} from '../../../utils/utils';
import FileIcon from '../../Common/FileIcon';
import ListyMetaRow from '../../Common/ListyMetaRow';
import { LISTY_ITEM_NO_HOVER } from '../../Common/listyItemClassNames';
import TruncatedTooltip from '../../Common/TruncatedTooltip';
import { Icon } from '../../Core/Icon';
import FileAntivirusStatusIcon from '../FileAntivirusStatusIcon';

interface FileDetailTabProps {
  file: File;
}

const TagContent = ({ tag }: { tag: TagType }) => {
  const { t } = useTranslation();
  return (
    <div className="flex cursor-help flex-row items-center justify-center lg:justify-between">
      <div />
      <span className="max-w-28">
        <TruncatedTooltip>{tag.title}</TruncatedTooltip>
      </span>
      <div className="flex flex-row items-center gap-1">
        {tag.isUsedForFileVisibility ? (
          <Tooltip title={t('common.tagIsUsedForFileVisibility')}>
            <div>
              <Icon name="Share2Icon" className="text-gray-dark h-3 w-3" />
            </div>
          </Tooltip>
        ) : (
          <div className="w-1" />
        )}
        {tag.publicTag ? (
          <Icon name="BuildingIcon" className="text-gray-dark h-3 w-3" />
        ) : (
          <div className="w-1" />
        )}
      </div>
    </div>
  );
};

const FileDetailTab: React.FC<FileDetailTabProps> = ({ file }) => {
  const {
    t,
    tags,
    isLoading,
    showMoreVersions,
    setShowMoreVersions,
    isShowMoreVersionsButtonVisible,
    sortedFileVersions,
    isLoadingFileVersions,
    fileTags,
    fileAccesses,
    totalFileAccesses,
    fileDescription,
    fileCreatedAt,
    fileSellInfo,
    versionsScrollRef,
    handleVersionsScroll,
    handleShareFiles,
    handleCreatePublicLink,
    onFileUpdated,
    isPublicLinkSettingEnabled,
    publicShareLink,
    classificationStatusMap,
    showAIDetails,
    setShowAIDetails,
    getTagExplanations,
    isManageAccessVisible,
    handleDescriptionChange,
    handleRestoreFileVersion,
    handleOpenFile,
    isFileMine,
    folderStats,
    isLoadingFolderStats,
    canRefreshFolderStats,
    loadFolderStats,
  } = useFileDetailTab({ file });
  const { resolvedColorScheme } = useColorScheme();
  const isDarkMode = resolvedColorScheme === 'dark';

  return (
    <OverlayScrollbarsComponent
      options={{
        scrollbars: {
          autoHide: 'leave',
        },
      }}
      defer
      style={{ height: '100%', overflow: 'auto' }}
    >
      <div className="flex h-full flex-col gap-6 pr-2.5">
        <FileAntivirusStatusIcon file={file} variant="banner" />
        <dl className="bg-azure-light text-blue-dark dark:bg-gray-light grid grid-cols-1 gap-2 rounded-md p-3">
          {fileCreatedAt && (
            <div className="grid grid-cols-2 gap-2">
              <dt className="text-right font-medium">{t('common.createdAt')}:</dt>
              <dd className="text-left">
                {dateFromTicks(fileCreatedAt).toLocaleDateString() || '-'}
              </dd>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <dt className="text-right font-medium">{t('common.lastModified')}:</dt>
            <dd className="text-left">
              {dateFromTicks(file?.lastModifiedAt).toLocaleDateString() || '-'}
            </dd>
          </div>
          <div className="grid grid-cols-2 items-start gap-2">
            <dt className="text-right font-medium">ID:</dt>
            <dd className="text-left">
              <Tooltip title={t('common.copyToClipboard')}>
                <Button
                  type="link"
                  size="small"
                  className="text-blue-dark! hover:text-azure! h-[22px] gap-2 p-0!"
                  onClick={() => {
                    copyToClipboard(file?.id.toString() || '');
                  }}
                >
                  <span className="max-w-24 truncate">{file?.id || '-'}</span>
                  <Icon name="CopyIcon" className="w-4" />
                </Button>
              </Tooltip>
            </dd>
          </div>
          {!file.isFolder && (
            <div className="grid grid-cols-2 gap-2">
              <dt className="text-right font-medium">{t('common.fileType')}:</dt>
              <dd className="text-left">{file?.fileType}</dd>
            </div>
          )}
          {file.isFolder &&
            (folderStats ? (
              <div
                data-testid="folder-content-stats"
                className="border-gray-medium/60 dark:bg-gray flex flex-col gap-2 rounded-md border bg-white/70 p-3"
              >
                <div className="grid grid-cols-2 gap-2">
                  <dt className="text-right font-medium">{t('common.files')}:</dt>
                  <dd className="text-left">{folderStats.fileCount}</dd>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <dt className="text-right font-medium">{t('common.subfolders')}:</dt>
                  <dd className="text-left">{folderStats.folderCount}</dd>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <dt className="text-right font-medium">{t('common.totalSize')}:</dt>
                  <dd className="text-left">{formatFileSize(folderStats.totalSize)}</dd>
                </div>
                <div className="flex justify-center pt-1">
                  <Tooltip
                    title={
                      canRefreshFolderStats
                        ? t('common.recalculate')
                        : t('common.recalculateCooldown')
                    }
                  >
                    <Button
                      type="text"
                      size="small"
                      aria-label={t('a11y.refreshFolderStats')}
                      loading={isLoadingFolderStats}
                      disabled={isLoadingFolderStats || !canRefreshFolderStats}
                      onClick={loadFolderStats}
                      icon={<Icon name="RotateCwIcon" className="w-3.5" />}
                    >
                      {t('common.recalculate')}
                    </Button>
                  </Tooltip>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 items-start gap-2">
                <dt className="text-right font-medium">{t('common.folderContent')}:</dt>
                <dd className="text-left">
                  <Button
                    type="link"
                    size="small"
                    className="text-blue-dark! hover:text-azure! h-[22px] p-0! underline"
                    loading={isLoadingFolderStats}
                    disabled={isLoadingFolderStats || !canRefreshFolderStats}
                    onClick={loadFolderStats}
                  >
                    {t('common.calculateFolderSize')}
                  </Button>
                </dd>
              </div>
            ))}

          {file.aiOperationStatus > 0 && (
            <div className="grid grid-cols-2 gap-2">
              <dt className="text-right font-medium">{t('common.classificationStatus')}:</dt>
              <dd className="text-left">
                <div>
                  <span>
                    {classificationStatusMap[
                      file.aiOperationStatus as keyof typeof classificationStatusMap
                    ] || t('enum.aiClassificationStatus.unknown')}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowAIDetails((prev) => !prev)}
                    className="px-2 text-sm hover:underline focus:outline-none"
                  >
                    <Icon name="InfoIcon" className="h-4 w-4" />
                  </button>
                </div>
              </dd>

              {showAIDetails && (
                <div className="col-span-2">
                  <pre className="border-gray-medium bg-gray w-full rounded border p-2 text-sm whitespace-pre-wrap">
                    {getTagExplanations()}
                  </pre>
                </div>
              )}
            </div>
          )}
        </dl>
        <div className={file.tags.length === 0 ? 'hidden' : 'flex flex-wrap gap-1'}>
          {fileTags.map((tagIg) => {
            const tag = tags.find((tg) => tg.id === tagIg.id);
            if (tag) {
              return (
                <Tag
                  key={tag.id}
                  style={getTagPresentationStyle(tag.color, { darkMode: isDarkMode })}
                >
                  {tagIg.explanation ? (
                    <Tooltip
                      title={
                        <div className="max-w-xs text-sm whitespace-pre-line">
                          {tagIg.explanation}
                        </div>
                      }
                      style={{ maxWidth: 300 }}
                    >
                      <div>
                        <TagContent tag={tag} />
                      </div>
                    </Tooltip>
                  ) : (
                    <TagContent tag={tag} />
                  )}
                </Tag>
              );
            }
            return null;
          })}
        </div>

        <div className="border-gray-medium flex h-48 w-full shrink-0 items-center justify-center rounded-md border">
          <FileIcon
            fileId={file.id}
            className="h-32 w-32"
            imageClassName="h-full! w-full!"
            isFolder={file.isFolder}
            isShortcut={file.isShortcut}
            fileExtension={removeShortcutExtension(file.fileExtension)}
            secretPreset={file.secretPreset}
            style={{
              color: getFolderHex(file),
            }}
          />
        </div>

        {isFileMine(file) &&
          (file.salesStatus === FileSalesStatusEnum.ForSale ||
            file.salesStatus === FileSalesStatusEnum.OnHold) &&
          fileSellInfo && (
            <Spin spinning={isLoading}>
              <div className="flex flex-col gap-2 pt-2.5">
                <label className="text-lg font-medium">{t('store.fileSalesOverview')}</label>

                <div className="border-gray-medium rounded-md border bg-white p-4 shadow-md">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="flex flex-col">
                      <span className="text-gray-dark text-sm">{t('store.totalSales')}</span>
                      <span className="text-l font-semibold">{fileSellInfo.unitsSold || 0}</span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-gray-dark text-sm">{t('store.fileRevenue')}</span>
                      <span className="text-l text-green font-semibold">
                        {printCurrency(fileSellInfo.currency || 'EUR')}
                        {printAmount(fileSellInfo.revenue as number)}
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-gray-dark text-sm">{t('store.unitRevenue')}</span>
                      <span className="text-l font-semibold">
                        {printCurrency(fileSellInfo.currency || 'EUR')}
                        {printAmount(fileSellInfo.price as number)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="self-end px-2">
                  <a
                    href={getPurchaseLink(file.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-align-right text-blue hover:underline"
                  >
                    {t('store.openStoreLink')}
                  </a>
                </div>
              </div>
            </Spin>
          )}

        {isFileMine(file) && (
          <div className="flex h-36 flex-col gap-3 pt-2.5">
            <div className="flex flex-row items-start justify-between gap-2">
              <span className="text-lg font-medium">{t('common.sharedItems')}</span>
              {isManageAccessVisible && (
                <Button
                  type="text"
                  className="underline"
                  onClick={() => {
                    handleShareFiles([file], {
                      onFilesUpdated: onFileUpdated,
                    });
                  }}
                >
                  {t('common.manageAccess')}
                </Button>
              )}
            </div>
            <Spin spinning={isLoading} tip={t('common.loading')}>
              <div
                className={clsx(
                  'grid grid-cols-4 gap-2 transition-all duration-200 ease-in-out',
                  isLoading && 'blur-sm',
                )}
              >
                {fileAccesses?.length === 0 ? (
                  <div className="text-gray-dark col-span-4 text-sm">
                    {t('common.notSharedYet')}
                  </div>
                ) : (
                  fileAccesses?.slice(0, 3)?.map((fileAccess) => (
                    <div key={fileAccess.id} className="flex flex-col items-center">
                      <Avatar
                        size={64}
                        className={clsx('bg-azure-hover!', isLoading && 'blur-sm!')}
                      >
                        {fileAccess.user ? (
                          <span className="uppercase">
                            {fileAccess.user?.firstName
                              ? `${fileAccess.user?.firstName.charAt(0)}${fileAccess.user?.lastName.charAt(0)}`
                              : fileAccess.user?.email?.charAt(0) || ''}
                          </span>
                        ) : (
                          <Icon name="UsersIcon" />
                        )}
                      </Avatar>
                      <p
                        className={clsx(
                          'mt-1 max-w-20 truncate text-center text-sm transition-all duration-200',
                          isLoading && 'blur-sm',
                        )}
                      >
                        <Tooltip
                          title={`${fileAccess.user?.firstName ? `${fileAccess.user?.firstName} ${fileAccess.user?.lastName}` : fileAccess.user?.email || fileAccess.group?.name}`}
                        >
                          {fileAccess.user?.firstName
                            ? `${fileAccess.user?.firstName} ${fileAccess.user?.lastName}`
                            : fileAccess.user?.email || fileAccess.group?.name}
                        </Tooltip>
                      </p>
                    </div>
                  ))
                )}

                {fileAccesses?.length > 3 && (
                  <Tooltip
                    title={
                      <div className="flex flex-col items-center">
                        {fileAccesses?.slice(3)?.map((fileAccess) => (
                          <div key={fileAccess.id}>
                            {fileAccess?.user?.firstName || fileAccess?.group?.name}
                          </div>
                        ))}
                        {fileAccesses?.length > 3 && <>and {totalFileAccesses - 4} more</>}
                      </div>
                    }
                  >
                    <Avatar
                      className="bg-azure-hover!"
                      size={64}
                      icon={<span className="text-xl">{`+${totalFileAccesses - 3}`}</span>}
                    />
                  </Tooltip>
                )}
              </div>
            </Spin>
          </div>
        )}

        {isFileMine(file) && isPublicLinkSettingEnabled && (
          <div className="flex flex-col gap-2 pt-2.5">
            <div className="flex flex-row items-center justify-between gap-2">
              <span className="text-lg font-medium">{t('publicShare.badgeSection')}</span>
              {publicShareLink ? (
                <div className="flex flex-row items-center gap-2">
                  <Tooltip title={t('publicShare.badgeActive')}>
                    <span className="text-blue-dark flex items-center gap-1 text-sm">
                      <Icon name="LinkIcon" className="h-4 w-4" />
                      {t('publicShare.badgeActive')}
                    </span>
                  </Tooltip>
                  <Button
                    type="text"
                    size="small"
                    onClick={() => handleCreatePublicLink(file, { onFilesUpdated: onFileUpdated })}
                  >
                    {t('publicShare.badgeSection')}
                  </Button>
                </div>
              ) : (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => handleCreatePublicLink(file, { onFilesUpdated: onFileUpdated })}
                >
                  {t('publicShare.createLink')}
                </Button>
              )}
            </div>
          </div>
        )}

        {isFileMine(file) && (
          <div className="border-gray-medium relative flex flex-col gap-3 border-t pt-2.5">
            <div className="flex flex-row items-start justify-between gap-2">
              <span className="text-lg font-medium">{t('common.versions')}</span>
            </div>
            <div
              ref={versionsScrollRef}
              onScroll={handleVersionsScroll}
              className={clsx(!showMoreVersions && 'max-h-[200px] overflow-y-auto')}
            >
              <Spin spinning={isLoadingFileVersions}>
                <Listy
                  items={sortedFileVersions}
                  rowKey="id"
                  className="pb-6!"
                  classNames={{ item: LISTY_ITEM_NO_HOVER }}
                  itemRender={(item, index) => {
                    const versionNumber = sortedFileVersions.length - index;
                    const isCurrentVersion = index === 0;

                    return (
                      <Skeleton avatar title={false} loading={isLoadingFileVersions} active>
                        <ListyMetaRow
                          avatar={
                            <Avatar className="bg-blue-dark!">
                              <Icon name="HistoryIcon" className="h-5 w-5" />
                            </Avatar>
                          }
                          title={
                            <div className="flex flex-col items-start">
                              <Tooltip
                                title={
                                  t('common.open') + ' ' + t('common.version') + ' ' + versionNumber
                                }
                              >
                                <Button
                                  type="link"
                                  size="small"
                                  className="text-blue-dark! hover:text-azure! p-0! font-medium!"
                                  onClick={() => {
                                    window.open(`/viewer/${file.id}?version=${item.id}`, '_blank');
                                  }}
                                >
                                  {t('common.version')} {versionNumber}
                                </Button>
                              </Tooltip>
                              {index === 0 ? (
                                <span className="text-gray-dark min-w-40 text-xs">
                                  {t('common.currentVersion')}
                                </span>
                              ) : (
                                <span className="text-gray-dark min-w-40 text-xs">
                                  {dateFromTicks(item.createdAt).toDateString() || '-'},{' '}
                                  {dateFromTicks(item.createdAt).toLocaleTimeString()}
                                </span>
                              )}
                            </div>
                          }
                          description={null}
                          actions={
                            <div className="flex items-center gap-1">
                              <Tooltip title={t('common.open')}>
                                <Button
                                  size="small"
                                  type="text"
                                  onClick={() => {
                                    window.open(`/viewer/${file.id}?version=${item.id}`, '_blank');
                                  }}
                                >
                                  <Icon name="SquareArrowOutUpRight" className="h-5 w-5" />
                                </Button>
                              </Tooltip>
                              {!isCurrentVersion ? (
                                <Tooltip title={t('common.restore')}>
                                  <Button
                                    size="small"
                                    type="text"
                                    onClick={() => {
                                      handleRestoreFileVersion(file.id, item.id, versionNumber);
                                    }}
                                  >
                                    <Icon name="ArchiveRestoreIcon" className="h-5 w-5" />
                                  </Button>
                                </Tooltip>
                              ) : null}
                            </div>
                          }
                        />
                      </Skeleton>
                    );
                  }}
                />
              </Spin>
              <div
                className={clsx(
                  'pointer-events-auto absolute right-0 bottom-0 left-0 flex w-full flex-row items-center justify-center bg-white/80 backdrop-blur-sm transition-all duration-200 ease-in-out',
                  sortedFileVersions.length > 3 && isShowMoreVersionsButtonVisible
                    ? 'translate-y-0'
                    : 'translate-y-full opacity-0',
                )}
              >
                <Button
                  type="link"
                  size="small"
                  className="text-blue-dark! hover:text-azure! text-sm!"
                  disabled={isLoadingFileVersions}
                  onClick={() => {
                    setShowMoreVersions((prev) => !prev);
                  }}
                >
                  {showMoreVersions ? t('common.showLessVersions') : t('common.showMoreVersions')}
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="border-gray-medium flex flex-col gap-2 border-t pt-2.5">
          <label className="text-lg font-medium">{t('common.description')}</label>
          {hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileEditDescription) ? (
            <div className="text-gray-dark text-sm">{t('common.descriptionNotEditable')}</div>
          ) : (
            <>
              <Input.TextArea
                disabled={!isFileMine(file)}
                rows={4}
                maxLength={DESCRIPTION_CHARACTERS_LIMIT}
                value={fileDescription}
                onChange={handleDescriptionChange}
              />
              <p className="text-gray-dark text-right text-sm">
                {fileDescription?.length} / {DESCRIPTION_CHARACTERS_LIMIT}
              </p>
            </>
          )}
        </div>
        <div className="pb-4">
          <Button
            type="primary"
            size="large"
            className="w-full"
            onClick={() => {
              handleOpenFile(file);
            }}
          >
            {t('common.open')}
          </Button>
        </div>
      </div>
    </OverlayScrollbarsComponent>
  );
};

export default FileDetailTab;
